import { supabase } from "../lib/supabaseClient";
import { User, UsageStats } from "../types";

// --- CONFIG & UTILS ---
export const isAuthMockMode = !supabase;
export const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
export const MONTHLY_LIMIT = 100; // Default fallback

// --- CACHED AUTH USER (60s TTL) ---
let _cachedAuthUser: { user: any; ts: number } | null = null;
const AUTH_CACHE_TTL = 60_000; // 60 seconds

export const getCachedAuthUser = async () => {
  if (!supabase) return null;
  const now = Date.now();
  if (_cachedAuthUser && (now - _cachedAuthUser.ts) < AUTH_CACHE_TTL) {
    return _cachedAuthUser.user;
  }
  const { data } = await supabase.auth.getUser();
  if (data?.user) {
    _cachedAuthUser = { user: data.user, ts: now };
  }
  return data?.user || null;
};

export const invalidateAuthCache = () => { _cachedAuthUser = null; };

export const PLAN_LIMITS: Record<string, number> = {
  free: 100,
  pro: 2000,
  premium: 25000,
};

let mockUser: User = {
  id: "guest",
  name: "Guest",
  email: "guest@neura.ai",
  plan: "free",
  approval_mode: 'smart',
};

export const checkAndTrackCredits = async (
  userId: string,
  reqCredits: number,
): Promise<{ creditsUsed: number; creditsRemaining: number }> => {
  if (isAuthMockMode || !supabase)
    return { creditsUsed: 0, creditsRemaining: 999 };

  const today = new Date();
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  try {
    // ⚡ PARALLEL: Run both DB queries simultaneously instead of sequentially
    const [usageResult, profileResult] = await Promise.all([
      supabase
        .from("user_monthly_usage")
        .select("credits_used")
        .eq("user_id", userId)
        .eq("month", currentMonth)
        .maybeSingle(),
      supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle(),
    ]);

    const { data: usageData, error: usageErr } = usageResult;
    const { data: profile, error: profileErr } = profileResult;

    if (usageErr && !usageErr.message.includes("does not exist")) {
      console.warn("Credit Check Warning:", usageErr);
    }
    if (profileErr && !profileErr.message.includes("does not exist")) {
      console.warn("Profile Check Warning:", profileErr);
    }

    let plan = profile?.plan?.toLowerCase() || "free";
    const expiresAt = profile?.expires_at || null;
    const now = new Date();

    // Check for plan expiration
    if (plan !== "free" && expiresAt) {
      const expiryDate = new Date(expiresAt);
      if (expiryDate < now) {
        // Plan has expired, treat as free
        plan = "free";
      }
    }

    let monthlyLimit = PLAN_LIMITS[plan] || MONTHLY_LIMIT;
    
    // Add bonus credits to the limit
    const bonusCredits = profile?.bonus_credits || 0;
    monthlyLimit += bonusCredits;

    const currentUsed = usageData?.credits_used || 0;

    if (reqCredits > 0 && currentUsed + reqCredits > monthlyLimit) {
      throw new Error(
        `Monthly limit reached (${monthlyLimit} credits for ${plan} plan). Please upgrade to continue.`,
      );
    }

    if (reqCredits > 50) {
      throw new Error(
        "Safety cap exceeded: A single request attempted to consume more than 50 credits.",
      );
    }

    return {
      creditsUsed: Math.ceil(currentUsed),
      creditsRemaining: Math.max(0, Math.floor(monthlyLimit - currentUsed)),
    };
  } catch (e) {
    throw e;
  }
};

export const incrementCredits = async (userId: string, amount: number) => {
  if (amount <= 0 || isAuthMockMode || !supabase) return;

  const today = new Date();
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const { error } = await supabase.rpc("increment_credits", {
    p_user: userId,
    p_month: currentMonth,
    p_amount: amount,
  });

  if (error) {
    console.warn("Credit Update Warn (might need RPC setup):", error.message);
  }
};

// --- AUTH SERVICES ---
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  if (!supabase) return { unsubscribe: () => {} };
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
  return subscription;
};

export const signInWithGoogle = async () => {
  if (isAuthMockMode) {
    console.log("Mock Mode: Simulating Google Login");
    return { isMock: true };
  }

  const { data, error } = await supabase!.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: window.location.origin,
      queryParams: {
        access_type: "offline",
        prompt: "consent",
      },
    },
  });

  if (error) console.error("Google Login Error:", error);
  return { data, error, isMock: false };
};

export const signOut = async () => {
  if (isAuthMockMode) {
    window.location.reload();
    return;
  }
  await supabase!.auth.signOut();
  window.location.reload();
};

export const getAuthSession = async () => {
  if (isAuthMockMode) return null;
  try {
    const {
      data: { session },
      error,
    } = await supabase!.auth.getSession();
    if (error) {
      console.warn("Supabase Auth Error in getSession:", error.message);
      if (
        error.message.toLowerCase().includes("refresh token") ||
        error.status === 400
      ) {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("sb-")) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        await supabase!.auth.signOut().catch(() => {});
        // Return null instead of reloading to prevent infinite loops
        return null;
      }
    }
    return session;
  } catch (e: any) {
    console.error("Exception in getAuthSession:", e);
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sb-")) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    await supabase!.auth.signOut().catch(() => {});
    return null;
  }
};

export const getUser = async (): Promise<User> => {
  if (isAuthMockMode) {
    await wait(500);
    return mockUser;
  }

  if (!supabase) throw new Error("Supabase client not initialized");

  let user = null;
  try {
    const { data, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.warn("Supabase Auth Error:", authError.message);
      if (
        authError.message.toLowerCase().includes("refresh token") ||
        authError.status === 400
      ) {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("sb-")) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        await supabase.auth.signOut().catch(() => {});
        // Fall through to anonymous sign-in instead of reloading
      }
    }
    user = data?.user;
  } catch (e: any) {
    console.error("Exception in getUser:", e);
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sb-")) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    await supabase.auth.signOut().catch(() => {});
  }

  if (!user) {
    const { data: anonUser, error: anonError } =
      await supabase.auth.signInAnonymously();
    if (anonError) {
      console.warn("Supabase Anon Sign-In Failed, falling back to Guest User:", anonError.message);
      return mockUser;
    }
    if (anonUser.user) {
      const { error } = await supabase
        .from("profiles")
        .insert({ id: anonUser.user.id, full_name: "Guest User" })
        .select()
        .single();
      return {
        id: anonUser.user.id,
        name: "Guest User",
        email: "guest@neura.ai",
        plan: "free",
      };
    }
    return mockUser;
  }

  const { data: profile } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", user.id)
    .maybeSingle();

  const metaName = user.user_metadata?.full_name || user.user_metadata?.name;
  const metaAvatar =
    user.user_metadata?.avatar_url || user.user_metadata?.picture;
  const rawName = profile?.full_name || metaName || "Guest User";
  let fullName = rawName;
  if (fullName.length < 4) {
    fullName = (fullName + " User").slice(0, 20);
  } else if (fullName.length > 20) {
    fullName = fullName.slice(0, 20).trim();
  }

  if (!profile) {
    await supabase
      .from("profiles")
      .upsert({ id: user.id, full_name: fullName });
  }

  let currentPlan = profile?.plan || "free";
  const expiresAt = profile?.expires_at;
  const now = new Date();

  // Handle plan expiration logic
  if (currentPlan !== "free" && expiresAt) {
    if (new Date(expiresAt) < now) {
      currentPlan = "free";
    }
  }

  return {
    id: user.id,
    name: fullName,
    email: user.email || "guest@neura.ai",
    plan: currentPlan,
    avatar: metaAvatar,
    billing_cycle: profile?.billing_cycle,
    billed_at: profile?.billed_at,
    expires_at: profile?.expires_at,
    approval_mode: user.user_metadata?.approval_mode || 'smart',
  };
};

export const updateUser = async (updates: Partial<User>): Promise<User> => {
  if (isAuthMockMode) {
    await wait(500);
    mockUser = { ...mockUser, ...updates };
    return mockUser;
  }

  const {
    data: { user },
  } = await supabase!.auth.getUser();
  if (!user) throw new Error("Not logged in");

  const profileUpdates: any = {};
  if (updates.name) profileUpdates.full_name = updates.name;
  if (updates.plan) profileUpdates.plan = updates.plan;
  if (updates.billing_cycle) profileUpdates.billing_cycle = updates.billing_cycle;
  if (updates.billed_at) profileUpdates.billed_at = updates.billed_at;
  if (updates.expires_at) profileUpdates.expires_at = updates.expires_at;
  
  if (Object.keys(profileUpdates).length > 0) {
    await supabase!
      .from("profiles")
      .update(profileUpdates)
      .eq("id", user.id);
  }

  if (updates.approval_mode) {
    const { error: authError } = await supabase!.auth.updateUser({
      data: { approval_mode: updates.approval_mode }
    });
    if (authError) {
      console.error("Failed to update user auth metadata:", authError.message);
    }
  }

  return getUser();
};

export const recordBilling = async (
  userId: string,
  plan: 'free' | 'pro' | 'team',
  cycle: 'monthly' | 'yearly',
  billedAt: string,
  expiresAt: string,
): Promise<User> => {
  if (isAuthMockMode) {
    mockUser = {
      ...mockUser,
      plan,
      billing_cycle: cycle,
      billed_at: billedAt,
      expires_at: expiresAt,
    };
    return mockUser;
  }

  const { error } = await supabase!
    .from("profiles")
    .update({
      plan,
      billing_cycle: cycle,
      billed_at: billedAt,
      expires_at: expiresAt,
    })
    .eq("id", userId);

  if (error) throw error;
  return getUser();
};

export const addBonusCredits = async (userId: string, amount: number): Promise<User> => {
  if (isAuthMockMode) {
    mockUser = { ...mockUser }; // No easy way to store bonus in mockUser without updating types
    return mockUser;
  }

  const { data: profile } = await supabase!
    .from("profiles")
    .select("bonus_credits")
    .eq("id", userId)
    .single();

  const currentBonus = profile?.bonus_credits || 0;

  const { error } = await supabase!
    .from("profiles")
    .update({ bonus_credits: currentBonus + amount })
    .eq("id", userId);

  if (error) throw error;
  return getUser();
};

export const getUsage = async (): Promise<UsageStats> => {
  if (isAuthMockMode) {
    return { requests: 12, maxRequests: MONTHLY_LIMIT };
  }

  const {
    data: { user },
  } = await supabase!.auth.getUser();
  if (!user) return { requests: 0, maxRequests: MONTHLY_LIMIT };

  const { creditsUsed, creditsRemaining } = await checkAndTrackCredits(user.id, 0);

  return {
    requests: Math.max(0, Math.floor(creditsRemaining)),
    maxRequests: Math.ceil(creditsUsed + Math.max(0, creditsRemaining)),
  };
};
